import { Piece, SocialPlacementChannel } from '../general-types/briefs.dto';

export type OwedPlacement =
  | { pieceLabel: string; kind: 'ugc' }
  | { pieceLabel: string; kind: 'social'; channel: SocialPlacementChannel };

export type LegacyCrossPost = {
  channel: SocialPlacementChannel;
  rate: number;
};

export type HireFreeze = {
  owed: OwedPlacement[];
  crossPosts: LegacyCrossPost[];
  /** Net amount frozen at hire, before VAT. */
  amount: number;
  acceptedChannels: SocialPlacementChannel[];
};

export type FreezeHireInput = {
  pieces?: readonly Piece[] | null;
  offeredChannels?: readonly SocialPlacementChannel[];
  requestedChannels?: readonly SocialPlacementChannel[];
  productionRate: number;
  rankMultiplier: number;
  audienceFees?: Partial<Record<SocialPlacementChannel, number>>;
  videosPerCreator?: number;
  primaryChannel?: SocialPlacementChannel | null;
  primaryAudienceFee?: number;
  crossPosts?: readonly LegacyCrossPost[];
};

export const SOCIAL_CHANNEL_ID: Record<SocialPlacementChannel, number> = {
  tiktok_post: 0,
  instagram_reel: 1,
  instagram_story: 2,
  youtube_shorts: 3,
  snapchat_spotlight: 4,
  snapchat_story: 5,
};

const CHANNEL_BY_ID: Record<number, SocialPlacementChannel> = {
  0: 'tiktok_post',
  1: 'instagram_reel',
  2: 'instagram_story',
  3: 'youtube_shorts',
  4: 'snapchat_spotlight',
  5: 'snapchat_story',
};

export const PLACEMENT_CHANNEL_LABEL: Record<SocialPlacementChannel, string> = {
  tiktok_post: 'TikTok Post',
  instagram_reel: 'Instagram Reel',
  instagram_story: 'Instagram Story',
  youtube_shorts: 'YouTube Shorts',
  snapchat_spotlight: 'Snapchat Spotlight',
  snapchat_story: 'Snapchat Story',
};

export function owedPlacementLabel(placement: OwedPlacement): string {
  return placement.kind === 'ugc' ? 'UGC' : PLACEMENT_CHANNEL_LABEL[placement.channel];
}

export function placementLabelForChannelId(channelId: number): string {
  const channel = CHANNEL_BY_ID[channelId];
  return channel ? PLACEMENT_CHANNEL_LABEL[channel] : 'Channel';
}

export function placementChannelsFromIds(ids: readonly number[]): SocialPlacementChannel[] {
  const channels: SocialPlacementChannel[] = [];
  const seen = new Set<SocialPlacementChannel>();
  for (const id of ids) {
    const channel = CHANNEL_BY_ID[id];
    if (!channel || seen.has(channel)) {
      continue;
    }
    seen.add(channel);
    channels.push(channel);
  }
  return channels;
}

/**
 * Social channels a creator offers when applying to a piece-list brief.
 *
 * Only channels on the list are kept. UGC is not an offer. An empty request
 * records nothing, which hire treats as not offered.
 */
export function channelsOfferedAtApply(
  requested: readonly number[],
  pieces: readonly Piece[],
): number[] {
  const allowed = new Set(socialChannelIdsOnPieces(pieces));
  const offered: number[] = [];
  const seen = new Set<number>();
  for (const raw of requested) {
    const channelId = Number(raw);
    if (!Number.isInteger(channelId) || channelId < 0 || seen.has(channelId) || !allowed.has(channelId)) {
      continue;
    }
    seen.add(channelId);
    offered.push(channelId);
  }
  return offered;
}

export function socialChannelIdsOnPieces(pieces: readonly Piece[]): number[] {
  const ids: number[] = [];
  const seen = new Set<number>();
  for (const piece of pieces) {
    for (const placement of piece.placements) {
      if (placement.kind !== 'social') {
        continue;
      }
      const id = SOCIAL_CHANNEL_ID[placement.channel];
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

/**
 * Owed placements and the net hire amount.
 *
 * A piece list wins over the legacy video count and primary channel. Dropping a
 * social channel removes that placement from every piece. UGC stays. A piece with
 * nothing left is not owed. The amount is one production charge, including rank,
 * per owed piece, plus the audience fee for each owed social placement.
 */
export function freezeHire(input: FreezeHireInput): HireFreeze {
  if (input.pieces && input.pieces.length > 0) {
    return freezePieceHire(input);
  }
  return freezeLegacyHire(input);
}

export function quotePieceListHire(input: {
  pieces: readonly Piece[];
  offeredChannelIds: readonly number[];
  acceptedChannelIds: readonly number[];
  productionRate: number;
  rankMultiplier: number;
  audienceFeeForChannelId: (channelId: number) => number;
}): HireFreeze {
  const audienceFees: Partial<Record<SocialPlacementChannel, number>> = {};
  for (const channelId of socialChannelIdsOnPieces(input.pieces)) {
    const channel = CHANNEL_BY_ID[channelId];
    audienceFees[channel] = input.audienceFeeForChannelId(channelId);
  }

  return freezeHire({
    pieces: input.pieces,
    offeredChannels: placementChannelsFromIds(input.offeredChannelIds),
    requestedChannels: placementChannelsFromIds(input.acceptedChannelIds),
    productionRate: input.productionRate,
    rankMultiplier: input.rankMultiplier,
    audienceFees,
  });
}

/** Pieces that still have a placement after the brand drops channels. UGC stays. */
export function piecesOwedAtHire(input: FreezeHireInput): Piece[] {
  const offered = new Set(input.offeredChannels ?? []);
  const accepted = new Set(
    uniqueChannels(input.requestedChannels ?? []).filter((channel) => offered.has(channel)),
  );
  const kept: Piece[] = [];
  for (const piece of input.pieces ?? []) {
    const placements = piece.placements.filter((placement) => {
      if (placement.kind === 'ugc') {
        return true;
      }
      return accepted.has(placement.channel);
    });
    if (placements.length === 0) {
      continue;
    }
    kept.push({ ...piece, placements });
  }
  return kept;
}

function freezePieceHire(input: FreezeHireInput): HireFreeze {
  const offered = new Set(input.offeredChannels ?? []);
  const acceptedChannels = uniqueChannels(input.requestedChannels ?? []).filter((channel) =>
    offered.has(channel),
  );
  const kept = piecesOwedAtHire(input);
  const owed: OwedPlacement[] = [];
  for (const piece of kept) {
    for (const placement of piece.placements) {
      if (placement.kind === 'ugc') {
        owed.push({ pieceLabel: piece.label, kind: 'ugc' });
      } else {
        owed.push({ pieceLabel: piece.label, kind: 'social', channel: placement.channel });
      }
    }
  }
  const owedPieceCount = kept.length;

  const production = input.productionRate * input.rankMultiplier * owedPieceCount;
  let audience = 0;
  for (const placement of owed) {
    if (placement.kind === 'social') {
      audience += input.audienceFees?.[placement.channel] ?? 0;
    }
  }

  return {
    owed,
    crossPosts: [],
    amount: production + audience,
    acceptedChannels,
  };
}

function freezeLegacyHire(input: FreezeHireInput): HireFreeze {
  const videos = Math.max(0, Math.trunc(input.videosPerCreator ?? 1));
  const owed: OwedPlacement[] = [];
  for (let index = 1; index <= videos; index += 1) {
    if (input.primaryChannel == null) {
      owed.push({ pieceLabel: `Video ${index}`, kind: 'ugc' });
    } else {
      owed.push({
        pieceLabel: `Video ${index}`,
        kind: 'social',
        channel: input.primaryChannel,
      });
    }
  }

  const production = input.productionRate * input.rankMultiplier;
  const audience = input.primaryChannel == null ? 0 : (input.primaryAudienceFee ?? 0);
  const crossPosts = [...(input.crossPosts ?? [])];
  const crossTotal = crossPosts.reduce((total, post) => total + post.rate, 0);

  return {
    owed,
    crossPosts,
    amount: (production + audience) * videos + crossTotal,
    acceptedChannels: input.primaryChannel == null ? [] : [input.primaryChannel],
  };
}

export type PieceUpload = {
  pieceLabel: string;
  accepted: boolean;
};

export type LivePost = {
  pieceLabel: string;
  channel: SocialPlacementChannel;
  valid: boolean;
};

export type PlacementCompletion = {
  pieceLabel: string;
  kind: 'ugc' | 'social';
  channel?: SocialPlacementChannel;
  complete: boolean;
};

export type HireSettlement = {
  /** One label per owed piece. A second placement does not add an upload. */
  uploadsRequired: string[];
  /** Pieces whose accepted video may be posted. */
  postingAllowed: string[];
  placements: PlacementCompletion[];
  /** One payout of the frozen amount, or null while any owed placement is open. */
  payout: { amount: number } | null;
};

/**
 * Which owed placements are complete, and whether the single payout is released.
 *
 * UGC completes when that piece's video is accepted. A social placement completes
 * when a valid live post of that accepted video is on that channel. Posting is
 * allowed only after acceptance. The payout amount is the amount frozen at hire.
 */
export function settleHire(input: {
  owed: readonly OwedPlacement[];
  uploads: readonly PieceUpload[];
  posts: readonly LivePost[];
  frozenAmount: number;
}): HireSettlement {
  const uploadsRequired = owedPieceLabels(input.owed);
  const uploadByPiece = acceptedUploadByPiece(input.uploads);
  const postingAllowed = uploadsRequired.filter((label) => {
    const owesSocial = input.owed.some(
      (placement) => placement.pieceLabel === label && placement.kind === 'social',
    );
    return owesSocial && uploadByPiece.get(label) === true;
  });

  const placements: PlacementCompletion[] = input.owed.map((placement) => {
    const accepted = uploadByPiece.get(placement.pieceLabel) === true;
    if (placement.kind === 'ugc') {
      return { pieceLabel: placement.pieceLabel, kind: 'ugc', complete: accepted };
    }
    const posted = accepted && input.posts.some(
      (post) =>
        post.pieceLabel === placement.pieceLabel &&
        post.channel === placement.channel &&
        post.valid,
    );
    return {
      pieceLabel: placement.pieceLabel,
      kind: 'social',
      channel: placement.channel,
      complete: posted,
    };
  });

  const ready = placements.length > 0 && placements.every((placement) => placement.complete);
  return {
    uploadsRequired,
    postingAllowed,
    placements,
    payout: ready ? { amount: input.frozenAmount } : null,
  };
}

/** A piece takes one file. A further placement links that file; it does not upload again. */
export function acceptsAnotherUpload(
  owed: readonly OwedPlacement[],
  uploads: readonly { pieceLabel: string }[],
  pieceLabel: string,
): boolean {
  if (!owedPieceLabels(owed).includes(pieceLabel)) {
    return false;
  }
  return !uploads.some((upload) => upload.pieceLabel === pieceLabel);
}

/** Posting a placement is allowed only after that piece's video is accepted. */
export function canPostPlacement(input: {
  owed: readonly OwedPlacement[];
  uploads: readonly PieceUpload[];
  pieceLabel: string;
  channel: SocialPlacementChannel;
}): boolean {
  const owesChannel = input.owed.some(
    (placement) =>
      placement.kind === 'social' &&
      placement.pieceLabel === input.pieceLabel &&
      placement.channel === input.channel,
  );
  if (!owesChannel) {
    return false;
  }
  return input.uploads.some(
    (upload) => upload.pieceLabel === input.pieceLabel && upload.accepted,
  );
}

export type LegacyLivePost = {
  channel: SocialPlacementChannel;
  valid: boolean;
};

/**
 * An old brief with no piece list. Social completes on valid posts of the
 * primary channel, counted against videosPerCreator. UGC completes on accepted
 * videos. A post on another channel does not count. The payout is the amount
 * frozen at hire.
 */
export function settleLegacyHire(input: {
  videosPerCreator: number;
  primaryChannel: SocialPlacementChannel | null;
  acceptedVideoCount: number;
  posts: readonly LegacyLivePost[];
  frozenAmount: number;
}): { complete: boolean; payout: { amount: number } | null } {
  const required = Math.max(0, Math.trunc(input.videosPerCreator));
  const done = input.primaryChannel == null
    ? input.acceptedVideoCount
    : input.posts.filter((post) => post.valid && post.channel === input.primaryChannel).length;
  const complete = required > 0 && done >= required;
  return {
    complete,
    payout: complete ? { amount: input.frozenAmount } : null,
  };
}

/**
 * How many original videos this creator must deliver.
 *
 * A hire snapshot counts one video per piece still owed, so three posts of two
 * pieces still allow two drafts. Before hire, a piece list counts its pieces.
 * A brief with no piece list uses videosPerCreator.
 */
export function videosRequired(input: {
  pieces?: readonly unknown[] | null;
  videosPerCreator?: number | null;
  /** Placements still owed on this hire. Omit when the creator has not been hired. */
  owed?: readonly { pieceLabel: string }[] | null;
}): number {
  if (input.owed != null) {
    const labels = new Set<string>();
    for (const placement of input.owed) {
      const label = placement.pieceLabel.trim();
      if (label.length > 0) {
        labels.add(label);
      }
    }
    return labels.size;
  }

  const pieceCount = input.pieces?.length ?? 0;
  if (pieceCount > 0) {
    return pieceCount;
  }

  const legacy = input.videosPerCreator ?? 0;
  if (typeof legacy !== 'number' || !Number.isFinite(legacy)) {
    return 0;
  }
  return Math.max(0, Math.trunc(legacy));
}

function owedPieceLabels(owed: readonly OwedPlacement[]): string[] {
  const labels: string[] = [];
  for (const placement of owed) {
    if (!labels.includes(placement.pieceLabel)) {
      labels.push(placement.pieceLabel);
    }
  }
  return labels;
}

function acceptedUploadByPiece(uploads: readonly PieceUpload[]): Map<string, boolean> {
  const uploadByPiece = new Map<string, boolean>();
  for (const upload of uploads) {
    const alreadyAccepted = uploadByPiece.get(upload.pieceLabel) === true;
    if (alreadyAccepted) {
      continue;
    }
    uploadByPiece.set(upload.pieceLabel, upload.accepted);
  }
  return uploadByPiece;
}

function uniqueChannels(channels: readonly SocialPlacementChannel[]): SocialPlacementChannel[] {
  const seen = new Set<SocialPlacementChannel>();
  const unique: SocialPlacementChannel[] = [];
  for (const channel of channels) {
    if (seen.has(channel)) {
      continue;
    }
    seen.add(channel);
    unique.push(channel);
  }
  return unique;
}
